<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-select-input
                col="4"
                :optionsList="branches"
                :placeholder="$t('PLACEHOLDERS.branch')"
                v-model="filterOptions.branch"
              />
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date')"
                v-model="filterOptions.date"
              />
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.name')"
                v-model.trim="filterOptions.name"
              />
              <!-- <base-select-input
                col="3"
                :optionsList="activeStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.is_active"
              /> -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.reservations") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <button
            type="button"
            class="v-btn v-btn--is-elevated v-btn--has-bg theme--light v-size--default"
            :disabled="isExporting"
            @click="exportReservationsPdf"
          >
            {{
              isExporting ? $t("TABLES.loadingData") : $t("BUTTONS.downloadPdf")
            }}
          </button>
          <router-link
            v-if="$can('reservations create', 'reservations')"
            to="/reservations/create"
            >{{ $t("PLACEHOLDERS.add_reservation") }}</router-link
          >
        </div>
      </div>

      <div class="mb-3 text-end" dir="ltr">
        <strong>{{ $t("PLACEHOLDERS.total_cost") }}: {{ totalCost }}</strong>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>{{ $t("TABLES.noData") }}</template>
        <template v-slot:[`item.id`]="{ index }">{{
          (paginations.current_page - 1) * paginations.items_per_page +
          index +
          1
        }}</template>

        <template v-slot:[`item.patient`]="{ item }">{{
          item.patient?.name || "-"
        }}</template>

        <template v-slot:[`item.reservation_type`]="{ item }">
          {{ getReservationTypeLabel(item.reservation_type) }}
        </template>

        <template v-slot:[`item.is_active`]="{ item }">
          <div
            class="activation"
            dir="ltr"
            style="z-index: 1"
            v-if="$can('reservations activate', 'reservations')"
          >
            <v-switch
              class="mt-2"
              color="success"
              v-model="item.is_active"
              hide-details
              @change="changeActivationStatus(item)"
            ></v-switch>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('reservations show', 'reservations')"
            >
              <template slot="title"
                ><span>{{ $t("BUTTONS.show") }}</span></template
              >
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('reservations edit', 'reservations')"
            >
              <template slot="title"
                ><span>{{ $t("BUTTONS.edit") }}</span></template
              >
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>
            <a-tooltip
              placement="bottom"
              v-if="$can('reservations delete', 'reservations')"
            >
              <template slot="title"
                ><span>{{ $t("BUTTONS.delete") }}</span></template
              >
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <template v-slot:top>
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{
                  $t("TITLES.DeleteConfirmingMessage", {
                    name: itemToDelete.patient?.name || itemToDelete.id,
                  })
                }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>
                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam($event)"
        :prev-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
        "
        :next-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllReservations",

  computed: {
    ...mapGetters({ getAppLocale: "AppLangModule/getAppLocale" }),
    activeStatuses() {
      return [
        { id: 1, name: this.$t("STATUS.active"), value: 1 },
        { id: 2, name: this.$t("STATUS.notActive"), value: 0 },
      ];
    },
  },

  data() {
    return {
      loading: false,
      isExporting: false,
      isWaitingRequest: false,
      filterFormIsActive: false,
      branches: [],
      filterOptions: {
        name: null,
        is_active: null,
        branch: null,
        date: null,
      },
      searchValue: "",
      totalCost: 0,
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.patient"),
          value: "patient",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.age"),
          value: "age",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.reservation_type"),
          value: "reservation_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.date"),
          value: "date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.time"),
          value: "time",
          align: "center",
          sortable: false,
        },
        // {
        //   text: this.$t("PLACEHOLDERS.status"),
        //   value: "is_active",
        //   align: "center",
        //   sortable: false,
        // },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      dialogDelete: false,
      itemToDelete: null,
      paginations: { current_page: 1, last_page: 1, items_per_page: 10 },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    async getBranches() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "branches",
          params: {
            page: 0,
            limit: 0,
          },
        });
        const payload = res.data?.data || {};
        this.branches = (payload.data || []).map((item) => ({
          id: item.id,
          name: item.name,
        }));
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message || "Failed to load branches"
        );
      }
    },
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/reservations/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.is_active = null;
      this.filterOptions.branch = null;
      this.filterOptions.date = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/reservations/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: { ...this.$route.query, page: pagenationValue },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async setTableRows() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "reservations",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.name,
            is_active: this.filterOptions.is_active?.value,
            branch_id: this.filterOptions.branch?.id,
            date: this.filterOptions.date,
          },
        });
        const payload = res.data?.data || {};
        const rows = payload.data || [];
        this.tableRows = rows;
        // this.paginations.last_page = payload.meta?.last_page || 1;
        // this.paginations.items_per_page = payload.meta?.per_page || 10;
        this.totalCost = payload.total_cost ?? 0;
      } catch (error) {
        this.totalCost = 0;
        this.$message.error(
          error?.response?.data?.message || "Failed to load data"
        );
      } finally {
        this.loading = false;
      }
    },
    async exportReservationsPdf() {
      this.isExporting = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "https://backend.opetagency.com/export-all-reservation-to-pdf",
          params: {
            branch_id: this.filterOptions.branch?.id,
            date: this.filterOptions.date,
          },
        });
        const pdfUrl = res?.data?.pdf;
        if (pdfUrl) {
          window.open(pdfUrl, "_blank");
          this.$message.success(this.$t("MESSAGES.done"));
        } else {
          this.$message.error("PDF URL not found");
        }
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message || "Failed to export PDF"
        );
      } finally {
        this.isExporting = false;
      }
    },
    getReservationTypeLabel(type) {
      const normalizedType = String(type || "")
        .trim()
        .toLowerCase();
      const reservationTypesMap = {
        consultation: "كشف",
        follow_up: "متابعة",
        followup: "متابعة",
        operation: "عملية",
        surgery: "عملية",
        online_consultation: "استشارة اونلاين",
        online: "استشارة اونلاين",
        checkup: "استشارة",
      };

      return reservationTypesMap[normalizedType] || type || "-";
    },
    async changeActivationStatus(item) {
      try {
        await this.$axios({
          method: "POST",
          url: `reservations/toggle/${item.id}`,
          data: new FormData(),
        });
        this.$message.success(this.$t("MESSAGES.changeActivation"));
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message || "Failed to update status"
        );
      } finally {
        this.setTableRows();
      }
    },
    editItem(item) {
      this.$router.push({ path: `/reservations/edit/${item.id}` });
    },
    showItem(item) {
      this.$router.push({ path: `/reservations/show/${item.id}` });
    },
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },
    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `reservations/${this.itemToDelete.id}`,
        });
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message || "Failed to delete item"
        );
      } finally {
        this.dialogDelete = false;
        this.setTableRows();
      }
    },
  },

  async created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    await this.getBranches();
    this.setTableRows();
  },
};
</script>
